import { useMemo, useState } from "react";
import { Badge, Button, Checkbox, Text, Wrap, WrapItem, useToast } from "@chakra-ui/react";
import { PageHeader, SectionCard, DataTable, EmptyState } from "../../../shared/ui";
import { AlertTriangle, MessageSquare } from "../../../shared/icons";
import { formatarDataHora, parseDataISO } from "../../../shared/lib/date";
import { CATEGORIAS_FEEDBACK } from "../../feedback/api/feedbackApi";
import { useAdminFeedbacksQuery } from "../hooks/useAdminFeedbacksQuery";
import { useSetFeedbackDoneMutation } from "../hooks/useSetFeedbackDoneMutation";

const CATEGORIA_LABEL = Object.fromEntries(CATEGORIAS_FEEDBACK.map((c) => [c.value, c.label]));

const CATEGORIA_COLOR = {
  suggestion: "brand",
  problem: "danger",
  praise: "success",
};

// Triagem: "Pendentes" é o padrão porque é a fila de trabalho — marcar um feedback como
// feito o tira da lista, e "Concluídos"/"Todos" continuam mostrando o histórico.
const FILTROS = [
  { value: "pendentes", label: "Pendentes", aceita: (f) => !f.done_at },
  { value: "concluidos", label: "Concluídos", aceita: (f) => Boolean(f.done_at) },
  { value: "todos", label: "Todos", aceita: () => true },
];

export default function AdminFeedbacksPage() {
  const toast = useToast();
  const { data: feedbacks = [], isLoading, isError } = useAdminFeedbacksQuery();
  const doneMutation = useSetFeedbackDoneMutation();
  const [filtro, setFiltro] = useState("pendentes");

  const contagem = useMemo(
    () => Object.fromEntries(FILTROS.map((f) => [f.value, feedbacks.filter(f.aceita).length])),
    [feedbacks]
  );
  const visiveis = feedbacks.filter(FILTROS.find((f) => f.value === filtro).aceita);

  const alternarFeito = (row) =>
    doneMutation.mutate(
      { id: row.id, done: !row.done_at },
      {
        onSuccess: () =>
          toast({
            title: row.done_at ? "Feedback reaberto" : "Feedback marcado como feito",
            status: "success",
            duration: 3000,
          }),
        onError: (error) =>
          toast({
            title: "Não foi possível atualizar o feedback",
            description: error.response?.data?.mensagem,
            status: "error",
            duration: 5000,
            isClosable: true,
          }),
      }
    );

  const colunas = [
    {
      key: "done_at",
      label: "Feito",
      render: (row) => (
        <Checkbox
          isChecked={Boolean(row.done_at)}
          isDisabled={doneMutation.isPending && doneMutation.variables?.id === row.id}
          onChange={() => alternarFeito(row)}
          aria-label={row.done_at ? `Reabrir feedback de ${row.author_email}` : `Marcar feedback de ${row.author_email} como feito`}
        />
      ),
    },
    {
      key: "created_at",
      label: "Data",
      render: (row) => formatarDataHora(parseDataISO(row.created_at)),
    },
    { key: "author_email", label: "Autor" },
    {
      key: "category",
      label: "Categoria",
      render: (row) => (
        <Badge variant="statusSubtle" colorScheme={CATEGORIA_COLOR[row.category]}>
          {CATEGORIA_LABEL[row.category] ?? row.category}
        </Badge>
      ),
    },
    { key: "page", label: "Página", render: (row) => row.page || "—" },
    {
      key: "message",
      label: "Mensagem",
      // Td da tabela não quebra linha por padrão — a mensagem pode ser longa e multilinha.
      render: (row) => (
        <Text
          whiteSpace="pre-wrap"
          minW="320px"
          color={row.done_at ? "app-muted-fg" : undefined}
          textDecoration={row.done_at ? "line-through" : undefined}
        >
          {row.message}
        </Text>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Feedbacks"
        description="Mensagens enviadas pelos médicos no botão de feedback — as 200 mais recentes primeiro. Marque como feito o que já foi tratado."
      />

      <SectionCard>
        {isError ? (
          <EmptyState
            icon={AlertTriangle}
            title="Não foi possível carregar os feedbacks."
            description="Tente novamente em alguns instantes."
          />
        ) : (
          <>
            <Wrap spacing={2} mb={4}>
              {FILTROS.map((f) => (
                <WrapItem key={f.value}>
                  <Button size="sm" variant={filtro === f.value ? "solid" : "outline"} onClick={() => setFiltro(f.value)}>
                    {f.label} ({contagem[f.value]})
                  </Button>
                </WrapItem>
              ))}
            </Wrap>
            <DataTable
              columns={colunas}
              rows={visiveis}
              isLoading={isLoading}
              emptyState={
                <EmptyState
                  icon={MessageSquare}
                  title={feedbacks.length === 0 ? "Nenhum feedback recebido ainda" : "Nada por aqui neste filtro"}
                />
              }
            />
          </>
        )}
      </SectionCard>
    </>
  );
}
